import type {
	FacetMultiSelectFilterValue,
	FilterConfig,
	SearchQueryParams,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import type {
	AppliedMultiSelectFilterValue,
	FilterStrategy,
} from '@pages/VendorsSearch/containers/FilterPills/strategies/filter-strategy';

export class FacetMultiSelectFilterStrategy implements FilterStrategy {
	static readonly type = 'facet-multi-select';
	type = FacetMultiSelectFilterStrategy.type;
	readonly data: AppliedMultiSelectFilterValue;
	constructor(filterValue: FacetMultiSelectFilterValue, config: FilterConfig) {
		const value = filterValue.reduce(
			(acc, { facetId }) => {
				const option = config.filterOptions.find((o) => o.id === facetId);
				if (option) {
					acc.push({
						facetId,
						facetName: option.name,
						facetValue: option.singular.slug,
					});
				}

				return acc;
			},
			[] as AppliedMultiSelectFilterValue['value'],
		);
		if (
			config.type !== FacetMultiSelectFilterStrategy.type ||
			value.length === 0 ||
			value.some((val) => !val.facetId || !val.facetName || !val.facetValue)
		) {
			throw new Error('Invalid AppliedMultiSelectFilterValue data');
		}
		this.data = {
			value,
			config,
		};
	}

	calculateCount(): number {
		return this.data.value.length;
	}

	getData() {
		return this.data;
	}

	getAppliedFilterPills() {
		const { value, config } = this.data;
		return value.map((val) => ({
			filterId: config.id,
			facetId: val.facetId,
			name: val.facetName,
		}));
	}

	getAppliedFacetIds() {
		return this.data.value.map((facet) => facet.facetId);
	}

	getSeoFacetIds() {
		return this.getAppliedFacetIds();
	}

	getAppliedFacetNames() {
		return this.data.value.map((val) => val.facetName);
	}

	getAppliedFacetValues() {
		return this.data.value.map((val) => val.facetValue);
	}

	getSearchQueryParams(): Partial<SearchQueryParams> {
		return {
			filters: this.data.value.map((val) => val.facetId),
		};
	}

	getUrlQueryParams(): { key: string; value: string }[] {
		return [
			{
				key: this.data.config.id,
				value: this.data.value.map((val) => val.facetValue).join('+'),
			},
		];
	}

	getAppliedFiltersForTracking(): { [p: string]: string | string[] } {
		const { value, config } = this.data;
		return {
			[config.id]: value.map((val) => val.facetValue),
		};
	}
}
